<template>
	<div class="grid gap-4">
		<FormKit
			type="loanlinkCurrency"
			name="assessedIncome"
			label="Toetsinkomen"
			validation="required"
		/>
		<div class="ivo-readonly">
			<span class="ivo-l">Aantal ondernemingen</span>
			<span class="text-sm">{{ businesses.length }}</span>
		</div>
		<FormKit type="text" name="dossierNumber" label="Dossiernummer" />
		<FormKit
			type="select"
			placeholder="Kies..."
			name="rekenexpert"
			label="Rekenexpert"
			select-icon="down"
			:options="rekenexpertOptions"
		/>
		<FormKit type="loanlinkDate" name="issueDate" label="Afgiftedatum" />

		<div>
			<p class="text-sm font-semibold mb-2">Ondernemingen</p>

			<div v-for="(biz, idx) in businesses" :key="idx" class="ivo-card">
				<div class="ivo-card-head">
					<span class="text-sm font-medium">Onderneming #{{ idx + 1 }}</span>
					<button type="button" class="ivo-rm" @click="removeBusiness(idx)">
						✕
					</button>
				</div>
				<div class="grid gap-3">
					<label class="formkit-outer max-w-[20em]">
						<span
							class="block text-gray-900 text-xs font-semibold mb-px dark:text-gray-100"
							>KvK-nummer</span
						>
						<div
							class="text-sm w-full py-1 px-2 rounded-sm border border-gray-300 flex items-center dark:border-gray-100"
						>
							<input
								v-model="biz.kvkNumber"
								class="appearance-none outline-hidden text-sm text-gray-900 bg-transparent grow border-none p-0 focus:ring-0 dark:text-gray-100"
							/>
						</div>
					</label>
					<label class="formkit-outer max-w-[20em]">
						<span
							class="block text-gray-900 text-xs font-semibold mb-px dark:text-gray-100"
							>Rechtsvorm</span
						>
						<div
							class="relative flex items-center border border-gray-300 rounded-sm dark:border-gray-100"
						>
							<select
								v-model="biz.legalStructure"
								class="appearance-none grow py-1 pl-2 pr-[2em] text-sm text-gray-900 text-ellipsis min-w-0 outline-hidden bg-transparent border-none focus:ring-0 dark:text-gray-100"
							>
								<option value="">Kies...</option>
								<option
									v-for="o in legalStructureOptions"
									:key="o.value"
									:value="o.value"
								>
									{{ o.label }}
								</option>
							</select>
						</div>
					</label>
					<label class="formkit-outer max-w-[20em]">
						<span
							class="block text-gray-900 text-xs font-semibold mb-px dark:text-gray-100"
							>Soort onderneming</span
						>
						<div
							class="relative flex items-center border border-gray-300 rounded-sm dark:border-gray-100"
						>
							<select
								v-model="biz.businessType"
								class="appearance-none grow py-1 pl-2 pr-[2em] text-sm text-gray-900 text-ellipsis min-w-0 outline-hidden bg-transparent border-none focus:ring-0 dark:text-gray-100"
							>
								<option value="">Kies...</option>
								<option
									v-for="o in businessTypeOptions"
									:key="o.value"
									:value="o.value"
								>
									{{ o.label }}
								</option>
							</select>
						</div>
					</label>
					<label class="formkit-outer max-w-[20em]">
						<span
							class="block text-gray-900 text-xs font-semibold mb-px dark:text-gray-100"
							>Startdatum</span
						>
						<AppFieldDate v-model="biz.startDate" />
					</label>
					<label
						class="formkit-outer max-w-[20em] inline-flex items-center gap-1.5"
					>
						<input
							v-model="biz.isDga"
							type="checkbox"
							class="appearance-none w-[1em] aspect-square border border-gray-300 rounded-sm checked:bg-violet-500 dark:border-gray-100"
						/>
						<span class="text-gray-900 text-xs font-semibold dark:text-gray-100"
							>DGA</span
						>
					</label>
				</div>
			</div>

			<button type="button" class="ivo-add" @click="addBusiness">
				+ Onderneming toevoegen
			</button>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { computed, onMounted } from "vue";
import AppFieldDate from "@/components/input/AppFieldDate.vue";
import type {
	IvoRekenexpertType,
	IvoBusinessType,
	OndernemingLegalStructureType,
} from "@loanlink-nl/model-app";
import type { InventarisationSelectOption } from "@/models/inventarisation/InventarisationSelectOption";

const props = defineProps<{
	entry?: { values?: Record<string, unknown> };
}>();

// Cleanup + sync bij mount
onMounted(() => {
	if (!props.entry?.values) return;
	// Verwijder legacy values.business
	if ("business" in props.entry.values) {
		delete props.entry.values.business;
	}
	// Sync numberOfBusinesses met businesses.length
	const arr = props.entry.values.businesses;
	props.entry.values.numberOfBusinesses = Array.isArray(arr) ? arr.length : 0;
});

interface Business {
	kvkNumber?: string;
	legalStructure?: string;
	businessType?: string;
	startDate?: string;
	isDga?: boolean;
}

const businesses = computed(
	() => (props.entry?.values?.businesses as Business[]) ?? [],
);

function addBusiness() {
	if (!props.entry?.values) return;
	if (!Array.isArray(props.entry.values.businesses)) {
		props.entry.values.businesses = [];
	}
	(props.entry.values.businesses as unknown[]).push({
		kvkNumber: "",
		legalStructure: "",
		businessType: "",
		startDate: "",
		isDga: false,
	});
	props.entry.values.numberOfBusinesses = (
		props.entry.values.businesses as unknown[]
	).length;
}

function removeBusiness(idx: number) {
	if (!props.entry?.values) return;
	const arr = props.entry.values.businesses as unknown[];
	arr.splice(idx, 1);
	props.entry.values.numberOfBusinesses = arr.length;
}

const rekenexpertOptions: InventarisationSelectOption<IvoRekenexpertType>[] = [
	{
		label: "Raadhuys Tax Legal Accounting",
		value: "01 Raadhuys Tax Legal Accounting",
	},
	{ label: "Pentrax Advies", value: "02 Pentrax Advies" },
	{ label: "Overviewz", value: "03 Overviewz" },
	{ label: "Zakelijk Inkomen", value: "04 Zakelijk Inkomen" },
	{ label: "Interne adviseur", value: "05 Interne adviseur" },
	{ label: "Viiz", value: "06 Viiz" },
];

const legalStructureOptions: InventarisationSelectOption<OndernemingLegalStructureType>[] =
	[
		{ label: "BV", value: "BV Besloten vennootschap" },
		{ label: "CV", value: "CV Commanditaire vennootschap" },
		{ label: "Eenmanszaak", value: "EMZ Eenmanszaak" },
		{ label: "Maatschap", value: "MA Maatschap" },
		{ label: "NV", value: "NV Naamloze vennootschap" },
		{ label: "Stichting", value: "ST Stichting" },
		{ label: "VOF", value: "VOF Vennootschap onder firma" },
	];

const businessTypeOptions: InventarisationSelectOption<IvoBusinessType>[] = [
	{
		label: "ZZP",
		value: "01 Zelfstandige Zonder Personeel (ZZP)",
	},
	{
		label: "Zelfstandige met personeel",
		value: "03 Zelfstandige met personeel",
	},
];
</script>

<style scoped>
.ivo-card {
	border: 1px solid var(--border, #e5e7eb);
	border-radius: 0.375rem;
	padding: 0.75rem;
	margin-bottom: 0.5rem;
}
.ivo-card-head {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 0.5rem;
}
.ivo-rm {
	background: none;
	border: none;
	color: var(--text-muted, #6b7280);
	cursor: pointer;
	font-size: 1rem;
}
.ivo-rm:hover {
	color: var(--color-fail, #dc2626);
}
.ivo-add {
	font-size: 0.8rem;
	color: var(--accent-primary, #7c3aed);
	background: none;
	border: 1px dashed var(--border, #e5e7eb);
	border-radius: 0.375rem;
	padding: 0.5rem 1rem;
	cursor: pointer;
	width: 100%;
	margin-top: 0.25rem;
}
.ivo-add:hover {
	background: var(--background-secondary, #f9fafb);
}
.ivo-readonly {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
}
</style>
